<template>
  <form @submit.prevent="handleSubmit" class="needs-validation" novalidate>
    <!-- Question Settings (hidden when using sub-questions) -->
    <div class="form-section mb-4" v-if="!hasSubQuestions">
      <h3 class="section-subtitle mb-3">Question Settings</h3>
      <div class="row g-3 align-items-end question-settings-row">
        <div class="col-md-4 col-lg-3">
          <label for="question_type" class="form-label">
            Question Type <span class="required-mark">*</span>
          </label>
          <select
            v-model="questionType"
            id="question_type"
            required
            class="form-select"
          >
            <option value="mcq">Multiple Choice</option>
            <option value="true_false">True/False</option>
            <option value="short_answer">Short Answer</option>
            <option value="matching">Matching</option>
          </select>
        </div>

        <div class="col-md-4 col-lg-4">
          <label for="difficulty" class="form-label">Difficulty</label>
          <select
            v-model="form.difficulty_level"
            id="difficulty"
            class="form-select"
          >
            <option value="">Select difficulty</option>
            <option value="remembering">Remembering</option>
            <option value="understanding">Understanding</option>
            <option value="analyzing">Analyzing</option>
            <option value="applying">Applying</option>
            <option value="evaluating">Evaluating</option>
            <option value="creating">Creating</option>
          </select>
        </div>

        <div class="col-md-4 col-lg-3">
          <label
            for="marks"
            class="form-label d-flex justify-content-between align-items-center"
          >
            <span>Marks</span>
            <span
              class="text-muted small ms-2"
              :title="
                ['matching', 'short_answer'].includes(questionType)
                  ? 'For matching and short answer, the value you enter is used exactly.'
                  : 'For other types, leave blank to auto-set from difficulty, or enter a custom value.'
              "
            >
              (hint)
            </span>
          </label>
          <div class="input-group input-group-sm" style="max-width: 180px">
            <input
              id="marks"
              type="number"
              min="0"
              class="form-control form-control-sm"
              v-model.number="form.marks"
            />
            <span class="input-group-text">points</span>
          </div>
        </div>

        <!-- Multi-answer toggle (MCQ only) -->
        <div class="col-md-4 col-lg-2" v-if="questionType === 'mcq'">
          <label class="form-label">MCQ Mode</label>
          <div class="form-check">
            <input
              id="multiple_answers"
              class="form-check-input"
              type="checkbox"
              v-model="form.multiple_answers"
            />
            <label class="form-check-label" for="multiple_answers">
              Multiple correct
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Question Content -->
    <div class="form-section mb-4">
      <h3 class="section-subtitle mb-3">Question Content</h3>

      <!-- Question Input -->
      <div class="d-flex align-items-center mb-3">
        <span class="badge bg-primary me-2">Q</span>

        <div class="flex-grow-1" v-if="form.is_math">
          <textarea
            ref="questionTextArea"
            class="form-control"
            v-model="questionText"
            :id="`question_${questionType}`"
            rows="4"
            placeholder="Type your full question here. Use the Math input below to insert equations at the cursor."
          ></textarea>

          <div class="mt-2">
            <label class="form-label mb-1">Math input</label>
            <math-field
              ref="mathField"
              virtual-keyboard-mode="onfocus"
              class="w-100 border rounded p-2"
              style="min-height: 48px"
            ></math-field>
            <div class="mt-2 d-flex gap-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="insertMathAtCursor('inline')"
              >
                Insert Inline ($...$)
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="insertMathAtCursor('block')"
              >
                Insert Block ($$...$$)
              </button>
            </div>
          </div>

          <div class="mt-2 p-2 border rounded" v-html="renderedPreview"></div>
        </div>

        <div class="flex-grow-1" v-else>
          <textarea
            v-if="questionType === 'short_answer' && isLongQuestionText"
            class="form-control"
            v-model="form.question"
            :id="`question_${questionType}`"
            rows="3"
            required
            placeholder="Enter your question here..."
          ></textarea>
          <input
            v-else
            class="form-control"
            v-model="form.question"
            :id="`question_${questionType}`"
            required
            placeholder="Enter your question here..."
          />
        </div>

        <label class="btn btn-outline-secondary ms-2" title="Add image">
          <input
            type="file"
            accept="image/*"
            @change="onQuestionImageChange"
            style="display: none"
          />
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="#888"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h7m4 0h5v5m-9 9l9-9"
            />
          </svg>
        </label>
      </div>

      <!-- Question Image Preview -->
      <div v-if="questionImagePreview" class="mb-3">
        <img
          :src="questionImagePreview"
          alt="Question Image Preview"
          class="img-thumbnail"
          style="max-width: 200px; max-height: 150px"
        />
        <button
          type="button"
          class="btn btn-sm btn-outline-danger ms-2"
          @click="removeQuestionImage"
          title="Remove image"
        >
          &times;
        </button>
      </div>

      <!-- MCQ & True/False Options -->
      <div
        v-if="['mcq', 'true_false'].includes(questionType) && !hasSubQuestions"
        class="mb-3"
      >
        <label class="form-label">Options</label>

        <div
          v-for="(option, idx) in form.options"
          :key="idx"
          class="input-group input-group-sm mb-2"
          style="max-width: 720px"
        >
          <div class="input-group-text">
            <!-- MCQ: radio OR checkbox -->
            <template v-if="questionType === 'mcq'">
              <input
                v-if="!form.multiple_answers"
                type="radio"
                :name="`correct_answer_group_${questionType}`"
                :value="option"
                v-model="mcqSingleCorrect"
                required
              />
              <input
                v-else
                type="checkbox"
                :value="option"
                v-model="form.correct_answer"
              />
            </template>

            <!-- True/False: always single -->
            <template v-else>
              <input
                type="radio"
                :name="`correct_answer_group_${questionType}`"
                :value="option"
                v-model="tfSingleCorrect"
                required
              />
            </template>
          </div>

          <template v-if="questionType === 'mcq'">
            <template v-if="form.is_math">
              <div class="flex-grow-1 p-1" style="min-width: 240px">
                <textarea
                  :ref="`optTextArea_${idx}`"
                  class="form-control form-control-sm mb-1"
                  v-model="form.options[idx]"
                  :placeholder="`Option ${String.fromCharCode(65 + idx)}`"
                  rows="2"
                  required
                ></textarea>
                <math-field
                  :ref="`optMathField_${idx}`"
                  virtual-keyboard-mode="onfocus"
                  class="w-100 border rounded p-1"
                  style="min-height: 36px"
                ></math-field>
                <div class="mt-1 d-flex gap-1">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="insertOptionMathAtCursor(idx, 'inline')"
                  >
                    Inline ($...$)
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    @click="insertOptionMathAtCursor(idx, 'block')"
                  >
                    Block ($$...$$)
                  </button>
                </div>
                <div
                  class="mt-1 p-2 border rounded bg-light"
                  v-html="renderMathString(form.options[idx])"
                ></div>
              </div>
            </template>

            <input
              v-else
              class="form-control form-control-sm"
              v-model="form.options[idx]"
              :placeholder="`Option ${String.fromCharCode(65 + idx)}`"
              required
              style="min-width: 200px; max-width: 600px"
            />
          </template>

          <input
            v-else
            type="text"
            class="form-control form-control-sm"
            :value="option"
            readonly
          />

          <!-- option image for MCQ -->
          <label
            v-if="questionType === 'mcq'"
            class="btn btn-outline-secondary btn-sm ms-2"
            title="Add image for this option"
          >
            <input
              type="file"
              accept="image/*"
              @change="onOptionImageChange($event, idx)"
              style="display: none"
            />
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="#888"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h7m4 0h5v5m-9 9l9-9"
              />
            </svg>
          </label>

          <div
            v-if="questionType === 'mcq' && optionImagePreviews[idx]"
            class="ms-2 d-flex align-items-center"
          >
            <img
              :src="optionImagePreviews[idx]"
              alt="Option Image Preview"
              class="img-thumbnail"
              style="max-width: 72px; max-height: 48px; object-fit: contain"
            />
            <button
              type="button"
              class="btn btn-outline-danger btn-sm ms-1"
              @click="clearOptionImage(idx)"
              title="Remove image"
            >
              &times;
            </button>
          </div>

          <button
            v-if="questionType === 'mcq' && form.options.length > 2"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="removeOption(idx)"
            title="Remove option"
          >
            &times;
          </button>
        </div>

        <button
          v-if="questionType === 'mcq'"
          type="button"
          class="btn btn-outline-success btn-sm mt-2"
          @click="addOption"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v8m-4-4h8" />
          </svg>
          Add option
        </button>
      </div>

      <!-- Short Answer -->
      <div v-else-if="questionType === 'short_answer'" class="mb-3">
        <label class="form-label">Expected Answer</label>

        <div v-if="form.is_math">
          <textarea
            ref="answerTextArea"
            class="form-control mb-2"
            v-model="answerText"
            rows="2"
            placeholder="Type the expected answer here (use math input below)"
            required
          ></textarea>

          <math-field
            ref="answerMathField"
            virtual-keyboard-mode="onfocus"
            class="w-100 border rounded p-2 mb-2"
            style="min-height: 36px"
          ></math-field>

          <div class="d-flex gap-2 mb-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="insertAnswerMathAtCursor('inline')"
            >
              Insert Inline ($...$)
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="insertAnswerMathAtCursor('block')"
            >
              Insert Block ($$...$$)
            </button>
          </div>

          <div
            class="p-2 border rounded bg-light"
            v-html="renderMathString(answerText)"
          ></div>
        </div>

        <div v-else>
          <textarea
            v-if="isLongShortAnswerText"
            class="form-control"
            v-model="shortAnswerSingle"
            rows="3"
            placeholder="Enter the expected answer"
            required
          ></textarea>
          <input
            v-else
            class="form-control"
            type="text"
            v-model="shortAnswerSingle"
            placeholder="Enter the expected answer"
            required
          />
        </div>

        <div class="mt-2 d-flex align-items-center gap-2">
          <label class="btn btn-success btn-sm mb-0">
            <input
              type="file"
              accept="image/*"
              style="display: none"
              @change="onCorrectAnswerImageChange"
            />
            Add answer image
          </label>
          <button
            v-if="correctAnswerImagePreview"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="removeCorrectAnswerImage"
          >
            &times; Remove
          </button>
        </div>

        <div v-if="correctAnswerImagePreview" class="mt-2">
          <img
            :src="correctAnswerImagePreview"
            alt="Correct Answer Image Preview"
            class="img-thumbnail"
            style="max-width: 200px; max-height: 150px"
          />
        </div>

        <div class="form-text mt-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4m0-4h.01" />
          </svg>
          Students must match this answer exactly
        </div>
      </div>

      <!-- Matching Question -->
      <div v-else-if="questionType === 'matching'" class="mb-3">
        <label class="form-label">Matching Items</label>

        <div class="row">
          <div class="col-md-6">
            <strong>Left Column</strong>
            <div
              v-for="(item, idx) in form.matching_items.left"
              :key="'left-' + idx"
              class="input-group input-group-sm mb-2"
            >
              <input
                class="form-control"
                v-model="form.matching_items.left[idx]"
                :placeholder="`Left ${idx + 1}`"
                required
              />
              <button
                v-if="form.matching_items.left.length > 2"
                type="button"
                class="btn btn-outline-danger btn-sm"
                @click="removeMatchingItem('left', idx)"
              >
                &times;
              </button>
            </div>
            <button
              type="button"
              class="btn btn-outline-success btn-sm mt-1"
              @click="addMatchingItem('left')"
            >
              + Add Left
            </button>
          </div>

          <div class="col-md-6">
            <strong>Right Column</strong>
            <div
              v-for="(item, idx) in form.matching_items.right"
              :key="'right-' + idx"
              class="input-group input-group-sm mb-2"
            >
              <input
                class="form-control"
                v-model="form.matching_items.right[idx]"
                :placeholder="`Right ${idx + 1}`"
                required
              />
              <button
                v-if="form.matching_items.right.length > 2"
                type="button"
                class="btn btn-outline-danger btn-sm"
                @click="removeMatchingItem('right', idx)"
              >
                &times;
              </button>
            </div>
            <button
              type="button"
              class="btn btn-outline-success btn-sm mt-1"
              @click="addMatchingItem('right')"
            >
              + Add Right
            </button>
          </div>
        </div>

        <div class="mt-3">
          <label class="form-label"
            >Matching Pairs (which left matches which right?)</label
          >

          <div
            v-for="(pair, idx) in form.matching_pairs"
            :key="'pair-' + idx"
            class="row align-items-center mb-2"
          >
            <div class="col-5">
              <select
                v-model.number="pair.left_index"
                class="form-select form-select-sm"
              >
                <option :value="null" disabled>Select Left</option>
                <option
                  v-for="(item, lidx) in form.matching_items.left"
                  :key="'lopt-' + lidx"
                  :value="lidx"
                >
                  {{ item || `Left ${lidx + 1}` }}
                </option>
              </select>
            </div>

            <div class="col-2 text-center">→</div>

            <div class="col-5">
              <select
                v-model.number="pair.right_index"
                class="form-select form-select-sm"
              >
                <option :value="null" disabled>Select Right</option>
                <option
                  v-for="(item, ridx) in form.matching_items.right"
                  :key="'ropt-' + ridx"
                  :value="ridx"
                >
                  {{ item || `Right ${ridx + 1}` }}
                </option>
              </select>
            </div>

            <div class="col-12 mt-1">
              <button
                v-if="form.matching_pairs.length > 1"
                type="button"
                class="btn btn-outline-danger btn-sm"
                @click="removeMatchingPair(idx)"
              >
                &times; Remove Pair
              </button>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-outline-success btn-sm mt-2"
            @click="addMatchingPair"
          >
            + Add Pair
          </button>
        </div>
      </div>

      <!-- Sub-questions (optional) -->
      <div class="form-section mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h3 class="section-subtitle mb-0">Sub-questions</h3>
          <div class="form-check form-switch">
            <input
              class="form-check-input"
              type="checkbox"
              id="has_sub_questions"
              v-model="hasSubQuestions"
            />
            <label class="form-check-label" for="has_sub_questions">
              This question has sub-questions (e.g. passage with follow-up
              items)
            </label>
          </div>
        </div>
        <SubQuestionsEditor v-if="hasSubQuestions" v-model="subQuestions" />
      </div>
    </div>

    <!-- Alerts -->
    <div class="alert alert-danger" v-if="error">{{ error }}</div>
    <div class="alert alert-success" v-if="success">
      Question saved successfully!
    </div>

    <!-- Submit Buttons -->
    <div class="d-flex justify-content-between mt-4">
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="$emit('cancel')"
        v-if="!hideCancel"
      >
        Cancel
      </button>
      <button type="submit" class="btn btn-primary" :disabled="success">
        {{ isEditMode ? "Update" : "Save" }} Question
      </button>
    </div>
  </form>
</template>

<script>
import "mathlive";
import katex from "katex";
import "katex/dist/katex.min.css";
import axios from "axios";
import SubQuestionsEditor from "./SubQuestionsEditor.vue";
import { renderMath } from "@/utils/mathRenderer";

import {
  normalizeMatchingItems,
  normalizeMatchingPairs,
  normalizeCorrectAnswerArray,
  normalizeCorrectAnswerSingle,
} from "@/utils/questionNormalize";

export default {
  name: "QuestionFormWithMathKaTeX",
  components: { SubQuestionsEditor },
  props: {
    subjectId: { type: [Number, String], required: true },
    topicId: { type: [Number, String], required: true },
    selectedGradeFilter: { type: [Number, String], default: "" },
    editQuestion: { type: Object, default: null },
    compact: { type: Boolean, default: false },
    hideMeta: { type: Boolean, default: false },
    hideCancel: { type: Boolean, default: false },
  },
  data() {
    return {
      questionType: "mcq",
      form: {
        id: null,
        question: "",
        options: ["", ""],

        // ALWAYS keep correct_answer as array in state
        correct_answer: [],

        explanation: "",
        difficulty_level: "remembering",
        marks: 1,
        is_math: false,
        is_chemistry: false,
        multiple_answers: false,
        is_required: false,

        matching_items: { left: ["", ""], right: ["", ""] },
        matching_pairs: [{ left_index: null, right_index: null }],
      },

      answerText: "",
      questionImage: null,
      correctAnswerImage: null,
      optionImages: [],
      optionImagePreviews: [],

      questionImagePreview: "",
      correctAnswerImagePreview: "",

      error: "",
      success: false,
      isEditMode: false,

      questionText: "",
      renderedPreview: "",

      hasSubQuestions: false,
      subQuestions: [],
    };
  },
  computed: {
    isLongQuestionText() {
      const text =
        this.form && typeof this.form.question === "string"
          ? this.form.question
          : "";
      return text.length > 80;
    },
    isLongShortAnswerText() {
      const text = this.shortAnswerSingle || "";
      return text.length > 80;
    },

    // Single-answer binding for MCQ radio (still stores in array internally)
    mcqSingleCorrect: {
      get() {
        return String(this.form.correct_answer?.[0] ?? "");
      },
      set(val) {
        this.form.correct_answer = val ? [val] : [];
      },
    },

    // True/False single binding
    tfSingleCorrect: {
      get() {
        return String(this.form.correct_answer?.[0] ?? "");
      },
      set(val) {
        this.form.correct_answer = val ? [val] : [];
      },
    },

    // Short answer uses first element
    shortAnswerSingle: {
      get() {
        return String(this.form.correct_answer?.[0] ?? "");
      },
      set(val) {
        this.form.correct_answer = [String(val ?? "")];
      },
    },
  },
  mounted() {
    if (!this.editQuestion) return;
    this.loadEditQuestion(this.editQuestion);
  },
  methods: {
    loadEditQuestion(q) {
      this.isEditMode = true;

      // 1) set type first
      this.questionType = q.question_type || "mcq";
      this.form.id = q.id ?? null;

      // 2) base fields
      this.form.question = q.question || "";
      this.form.explanation = q.explanation || "";
      this.form.difficulty_level = q.difficulty_level || "remembering";
      this.form.marks =
        typeof q.marks === "number"
          ? q.marks
          : q.marks
          ? Number(q.marks) || 1
          : 1;
      this.form.is_math = !!q.is_math;
      this.form.is_chemistry = !!q.is_chemistry;
      this.form.multiple_answers = !!q.multiple_answers;
      this.form.is_required = !!q.is_required || !!q.required;

      // 3) type-specific normalization
      if (this.questionType === "true_false") {
        this.form.options = ["true", "false"];
        const ca = normalizeCorrectAnswerSingle(q.correct_answer);
        this.form.correct_answer = ca ? [ca] : [];
      } else if (this.questionType === "mcq") {
        // options may be strings or [{text,image}]
        const rawOpts = Array.isArray(q.options) ? q.options : ["", ""];
        this.form.options = rawOpts
          .filter((v) => v !== null && v !== undefined)
          .map((opt) =>
            typeof opt === "object"
              ? String(opt.text ?? "").trim()
              : String(opt).trim(),
          );

        if (!this.form.options.length) this.form.options = ["", ""];

        // correct_answer now json array (or legacy scalar)
        const caArr = normalizeCorrectAnswerArray(q.correct_answer);
        // keep only values that exist in options
        const filtered = caArr.filter((v) => this.form.options.includes(v));
        this.form.correct_answer = filtered.length
          ? filtered
          : this.form.options[0]
          ? [this.form.options[0]]
          : [];

        // option image previews in edit mode (if stored)
        if (Array.isArray(q.options)) {
          this.optionImagePreviews = q.options.map((opt) => {
            if (opt && typeof opt === "object" && opt.image)
              return this.getStoredImageUrl(opt.image);
            return "";
          });
          this.optionImages = q.options.map(() => null);
        } else {
          this.optionImages = this.form.options.map(() => null);
          this.optionImagePreviews = this.form.options.map(() => "");
        }
      } else if (this.questionType === "short_answer") {
        const ca = normalizeCorrectAnswerSingle(q.correct_answer);
        this.form.correct_answer = [ca || ""];
        if (this.form.is_math) this.answerText = ca || "";
      } else if (this.questionType === "matching") {
        // backend stores matching in options + correct_answer
        this.form.matching_items = normalizeMatchingItems(
          q.options ?? q.matching_items,
        );
        this.form.matching_pairs = normalizeMatchingPairs(
          q.correct_answer ?? q.matching_pairs,
        );

        if (
          !Array.isArray(this.form.matching_pairs) ||
          !this.form.matching_pairs.length
        ) {
          this.form.matching_pairs = [{ left_index: null, right_index: null }];
        }
      }

      // images
      if (q.question_image_url)
        this.questionImagePreview = q.question_image_url;
      if (q.correct_answer_image_url)
        this.correctAnswerImagePreview = q.correct_answer_image_url;

      // sub questions
      if (Array.isArray(q.sub_questions) && q.sub_questions.length) {
        this.hasSubQuestions = true;
        this.subQuestions = q.sub_questions.map((sq) => ({
          id: sq.id,
          question: sq.question || "",
          question_type: sq.question_type || "short_answer",
          options: Array.isArray(sq.options) ? [...sq.options] : ["", ""],
          correct_answer: sq.correct_answer ?? "",
          difficulty_level: sq.difficulty_level || "",
          marks:
            typeof sq.marks === "number"
              ? sq.marks
              : sq.marks
              ? Number(sq.marks) || 1
              : 1,
        }));
      }

      this.updatePreview();
    },

    getStoredImageUrl(path) {
      if (!path) return "";
      if (path.startsWith("http://") || path.startsWith("https://"))
        return path;
      try {
        const base = (axios.defaults.baseURL || "").replace(/\/?api\/?$/i, "");
        if (base) return `${base}/storage/${path}`;
      } catch {}
      return `/storage/${path}`;
    },

    renderMathString(str) {
      return renderMath(str);
    },

    updatePreview() {
      const source = this.form.is_math ? this.questionText : this.form.question;
      if (!source) {
        this.renderedPreview = "";
        return;
      }

      if (this.form.is_math && !source.includes("$")) {
        try {
          this.renderedPreview = katex.renderToString(source, {
            throwOnError: false,
            displayMode: false,
          });
        } catch {
          this.renderedPreview = source;
        }
        return;
      }

      let html = source;

      html = html.replace(/\$\$(.+?)\$\$/gs, (match, tex) => {
        try {
          return katex.renderToString(tex, {
            throwOnError: false,
            displayMode: true,
          });
        } catch {
          return match;
        }
      });

      html = html.replace(/\$(.+?)\$/g, (match, tex) => {
        try {
          return katex.renderToString(tex, {
            throwOnError: false,
            displayMode: false,
          });
        } catch {
          return match;
        }
      });

      this.renderedPreview = html;
    },

    insertMathAtCursor(mode = "inline") {
      const field = this.$refs.mathField;
      const area = this.$refs.questionTextArea;
      if (!area || !field) return;

      const tex = (field.value || "").trim();
      if (!tex) return;

      const start = area.selectionStart ?? this.questionText.length;
      const end = area.selectionEnd ?? this.questionText.length;
      const before = this.questionText.slice(0, start);
      const after = this.questionText.slice(end);

      const wrapped = mode === "block" ? `\n$$${tex}$$\n` : ` $${tex}$ `;
      this.questionText = before + wrapped + after;

      this.$nextTick(() => {
        try {
          const pos = (before + wrapped).length;
          area.focus();
          area.setSelectionRange(pos, pos);
        } catch {}
      });

      try {
        field.value = "";
      } catch {}
      this.updatePreview();
    },

    insertAnswerMathAtCursor(mode = "inline") {
      const mfRef = this.$refs.answerMathField;
      const taRef = this.$refs.answerTextArea;
      const field = Array.isArray(mfRef) ? mfRef[0] : mfRef;
      const area = Array.isArray(taRef) ? taRef[0] : taRef;
      if (!field || !area) return;

      const tex = (field.value || "").trim();
      if (!tex) return;

      const current = String(this.answerText ?? "");
      const start = area.selectionStart ?? current.length;
      const end = area.selectionEnd ?? current.length;
      const before = current.slice(0, start);
      const after = current.slice(end);
      const wrapped = mode === "block" ? `\n$$${tex}$$\n` : ` $${tex}$ `;
      this.answerText = before + wrapped + after;

      this.$nextTick(() => {
        try {
          const pos = (before + wrapped).length;
          area.focus();
          area.setSelectionRange(pos, pos);
        } catch {}
      });

      try {
        field.value = "";
      } catch {}
    },

    insertOptionMathAtCursor(idx, mode = "inline") {
      const mfRef = this.$refs[`optMathField_${idx}`];
      const taRef = this.$refs[`optTextArea_${idx}`];
      const field = Array.isArray(mfRef) ? mfRef[0] : mfRef;
      const area = Array.isArray(taRef) ? taRef[0] : taRef;
      if (!field || !area) return;

      const tex = (field.value || "").trim();
      if (!tex) return;

      const current = String(this.form.options[idx] ?? "");
      const start = area.selectionStart ?? current.length;
      const end = area.selectionEnd ?? current.length;
      const before = current.slice(0, start);
      const after = current.slice(end);
      const wrapped = mode === "block" ? `\n$$${tex}$$\n` : ` $${tex}$ `;
      const next = before + wrapped + after;

      this.form.options.splice(idx, 1, next);

      this.$nextTick(() => {
        try {
          const pos = (before + wrapped).length;
          area.focus();
          area.setSelectionRange(pos, pos);
        } catch {}
      });

      try {
        field.value = "";
      } catch {}
    },

    addOption() {
      this.form.options.push("");
      this.optionImages.push(null);
      this.optionImagePreviews.push("");
    },

    removeOption(idx) {
      if (this.form.options.length <= 2) return;

      const removed = this.form.options.splice(idx, 1)[0];

      if (this.optionImages?.length > idx) this.optionImages.splice(idx, 1);
      if (this.optionImagePreviews?.length > idx) {
        const url = this.optionImagePreviews[idx];
        if (url && typeof URL !== "undefined") {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }
        this.optionImagePreviews.splice(idx, 1);
      }

      if (this.form.multiple_answers) {
        this.form.correct_answer = (this.form.correct_answer || []).filter(
          (v) => v !== removed,
        );
      } else {
        if (this.form.correct_answer?.[0] === removed)
          this.form.correct_answer = [];
      }
    },

    onOptionImageChange(e, idx) {
      const file = e.target.files?.[0];
      if (!this.optionImages) this.optionImages = [];
      this.optionImages.splice(idx, 1, file || null);

      if (!this.optionImagePreviews) this.optionImagePreviews = [];
      const existingUrl = this.optionImagePreviews[idx];
      if (existingUrl && typeof URL !== "undefined") {
        try {
          URL.revokeObjectURL(existingUrl);
        } catch {}
      }
      this.optionImagePreviews.splice(
        idx,
        1,
        file ? URL.createObjectURL(file) : "",
      );
    },

    clearOptionImage(idx) {
      if (this.optionImages?.length > idx)
        this.optionImages.splice(idx, 1, null);
      if (this.optionImagePreviews?.length > idx) {
        const url = this.optionImagePreviews[idx];
        if (url && typeof URL !== "undefined") {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }
        this.optionImagePreviews.splice(idx, 1, "");
      }
    },

    onQuestionImageChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.questionImage = file;
      this.questionImagePreview = URL.createObjectURL(file);
    },

    removeQuestionImage() {
      this.questionImage = null;
      this.questionImagePreview = "";
    },

    onCorrectAnswerImageChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.correctAnswerImage = file;
      this.correctAnswerImagePreview = URL.createObjectURL(file);
    },

    removeCorrectAnswerImage() {
      this.correctAnswerImage = null;
      this.correctAnswerImagePreview = "";
    },

    addMatchingItem(column) {
      this.form.matching_items[column].push("");
    },

    removeMatchingItem(column, idx) {
      if (this.form.matching_items[column].length <= 2) return;

      this.form.matching_items[column].splice(idx, 1);

      this.form.matching_pairs = (this.form.matching_pairs || []).filter(
        (pair) => {
          if (column === "left" && pair.left_index === idx) return false;
          if (column === "right" && pair.right_index === idx) return false;
          return true;
        },
      );

      this.form.matching_pairs.forEach((pair) => {
        if (column === "left" && pair.left_index > idx) pair.left_index--;
        if (column === "right" && pair.right_index > idx) pair.right_index--;
      });

      if (!this.form.matching_pairs.length) {
        this.form.matching_pairs = [{ left_index: null, right_index: null }];
      }
    },

    addMatchingPair() {
      this.form.matching_pairs.push({ left_index: null, right_index: null });
    },

    removeMatchingPair(idx) {
      if (this.form.matching_pairs.length <= 1) return;
      this.form.matching_pairs.splice(idx, 1);
    },

    async handleSubmit() {
      this.error = "";
      this.success = false;

      if (!this.selectedGradeFilter) {
        const msg = "Please select a grade level before adding a question.";
        this.error = msg;
        this.$emit("show-toast", { message: msg, type: "danger" });
        return;
      }

      // ---- Frontend validation (basic) ----
      if (!this.hasSubQuestions) {
        if (this.questionType === "mcq") {
          if (!Array.isArray(this.form.options) || this.form.options.length < 2) {
            const msg = "At least two options are required.";
            this.error = msg;
            this.$emit("show-toast", { message: msg, type: "danger" });
            return;
          }

          const ca = Array.isArray(this.form.correct_answer)
            ? this.form.correct_answer
            : [];
          const selected = ca.filter((v) => this.form.options.includes(v));

          if (!this.form.multiple_answers) {
            if (selected.length !== 1) {
              const msg = "Please select exactly one correct answer.";
              this.error = msg;
              this.$emit("show-toast", { message: msg, type: "danger" });
              return;
            }
          } else {
            if (selected.length < 2) {
              const msg = "Please select at least two correct answers.";
              this.error = msg;
              this.$emit("show-toast", { message: msg, type: "danger" });
              return;
            }
          }
        }

        if (this.questionType === "true_false") {
          const v = String(this.form.correct_answer?.[0] ?? "");
          if (!["true", "false"].includes(v)) {
            const msg = "Please select True or False as the correct answer.";
            this.error = msg;
            this.$emit("show-toast", { message: msg, type: "danger" });
            return;
          }
        }

        // Normalize MCQ options: fill blanks with labels
        if (this.questionType === "mcq") {
          this.form.options = this.form.options.map((opt, idx) => {
            const val = (opt || "").toString().trim();
            return val !== "" ? val : `Option ${String.fromCharCode(65 + idx)}`;
          });

          // Clean correct_answer against final options
          this.form.correct_answer = (this.form.correct_answer || []).filter(
            (v) => this.form.options.includes(v),
          );

          if (
            !this.form.multiple_answers &&
            this.form.correct_answer.length !== 1
          ) {
            this.form.correct_answer = this.form.options[0]
              ? [this.form.options[0]]
              : [];
          }
        }

        // For short_answer + math mode
        if (this.questionType === "short_answer" && this.form.is_math) {
          this.shortAnswerSingle =
            this.answerText || this.shortAnswerSingle || "";
        }
      }

      const formData = new FormData();

      formData.append("question_type", this.questionType);
      formData.append(
        "question",
        this.form.is_math ? this.questionText : this.form.question,
      );

      // ✅ correct_answer + options for matching must be sent as arrays/objects (not JSON strings)
      if (this.questionType === "matching") {
        const safePairs = normalizeMatchingPairs(this.form.matching_pairs);
        const filteredPairs = safePairs.filter(
          (p) => p.left_index !== null && p.right_index !== null,
        );

        if (filteredPairs.length < 1) {
          const msg = "Please create at least one matching pair.";
          this.error = msg;
          this.$emit("show-toast", { message: msg, type: "danger" });
          return;
        }

        // options[left][i], options[right][i]
        (this.form.matching_items.left || []).forEach((val, i) => {
          formData.append(`options[left][${i}]`, String(val ?? ""));
        });
        (this.form.matching_items.right || []).forEach((val, i) => {
          formData.append(`options[right][${i}]`, String(val ?? ""));
        });

        // correct_answer[i][left_index], correct_answer[i][right_index]
        filteredPairs.forEach((pair, i) => {
          formData.append(
            `correct_answer[${i}][left_index]`,
            String(pair.left_index),
          );
          formData.append(
            `correct_answer[${i}][right_index]`,
            String(pair.right_index),
          );
        });
      } else {
        // correct_answer as array of strings (store full texts)
        const ca = Array.isArray(this.form.correct_answer)
          ? this.form.correct_answer
          : [];
        const cleaned = ca
          .map((v) => String(v ?? "").trim())
          .filter((v) => v !== "");

        if (
          this.questionType === "short_answer" ||
          this.questionType === "open_ended"
        ) {
          // nullable allowed: don't append if empty
          cleaned.forEach((val, i) =>
            formData.append(`correct_answer[${i}]`, val),
          );
        } else {
          // required types
          cleaned.forEach((val, i) =>
            formData.append(`correct_answer[${i}]`, val),
          );
        }

        // options for mcq/true_false
        if (["mcq", "true_false"].includes(this.questionType)) {
          this.form.options.forEach((opt, i) => {
            formData.append(`options[${i}]`, opt);
            if (this.questionType === "mcq" && this.optionImages?.[i]) {
              formData.append(`option_images[${i}]`, this.optionImages[i]);
            }
          });
        } else {
          formData.append("options[]", "");
        }
      }

      // marks
      const rawMarks = this.form.marks;
      if (this.hasSubQuestions) {
        formData.append("marks", "0");
      } else if (["matching", "short_answer"].includes(this.questionType)) {
        const m =
          rawMarks === null || rawMarks === undefined || rawMarks === ""
            ? 1
            : rawMarks;
        formData.append("marks", String(m));
      } else if (
        rawMarks !== null &&
        rawMarks !== undefined &&
        rawMarks !== ""
      ) {
        formData.append("marks", String(rawMarks));
      }

      formData.append("explanation", this.form.explanation || "");
      formData.append(
        "difficulty_level",
        this.form.difficulty_level || "remembering",
      );
      formData.append("is_math", this.form.is_math ? "1" : "0");
      formData.append("is_chemistry", this.form.is_chemistry ? "1" : "0");
      formData.append(
        "multiple_answers",
        this.form.multiple_answers ? "1" : "0",
      );
      formData.append("is_required", this.form.is_required ? "1" : "0");

      formData.append("subject_id", this.subjectId);
      formData.append("topic_id", this.topicId);
      formData.append(
        "grade_level_id",
        this.selectedGradeFilter !== "" ? String(this.selectedGradeFilter) : "",
      );

      formData.append("has_sub_questions", this.hasSubQuestions ? "1" : "0");

      // sub-questions (kept as you had it)
      const cleanedSubQuestions = [];
      if (this.hasSubQuestions) {
        (this.subQuestions || [])
          .filter((sq) => (sq.question || "").toString().trim() !== "")
          .forEach((sq) => {
            const base = {
              id: sq.id || null,
              question: sq.question || "",
              question_type: sq.question_type || "short_answer",
              marks: sq.marks ?? 1,
              difficulty_level: sq.difficulty_level || "",
            };

            const caArr = Array.isArray(sq.correct_answer)
              ? sq.correct_answer.map((v) => String(v ?? ""))
              : [String(sq.correct_answer ?? "")];

            if (base.question_type === "mcq") {
              base.options = Array.isArray(sq.options)
                ? sq.options.map((o) => (o || "").toString())
                : ["", ""];
              let selected = String(caArr[0] ?? "");
              if (base.options.length && !base.options.includes(selected)) {
                selected = base.options[0] || "";
              }
              base.correct_answer = selected ? [selected] : [];
            } else if (base.question_type === "true_false") {
              base.options = ["true", "false"];
              let selected = String(caArr[0] ?? "");
              if (!["true", "false"].includes(selected)) selected = "";
              base.correct_answer = selected ? [selected] : [];
            } else {
              base.options = null;
              const txt = String(caArr[0] ?? "");
              base.correct_answer = [txt];
            }

            cleanedSubQuestions.push(base);
          });
      }

      if (cleanedSubQuestions.length) {
        cleanedSubQuestions.forEach((sq, index) => {
          if (sq.id != null)
            formData.append(`sub_questions[${index}][id]`, String(sq.id));
          formData.append(
            `sub_questions[${index}][question]`,
            sq.question || "",
          );
          formData.append(
            `sub_questions[${index}][question_type]`,
            sq.question_type || "short_answer",
          );
          formData.append(
            `sub_questions[${index}][marks]`,
            sq.marks != null ? String(sq.marks) : "1",
          );
          formData.append(
            `sub_questions[${index}][difficulty_level]`,
            sq.difficulty_level || "",
          );

          if (sq.question_type === "mcq" || sq.question_type === "true_false") {
            (sq.options || []).forEach((opt, optIdx) => {
              formData.append(
                `sub_questions[${index}][options][${optIdx}]`,
                (opt || "").toString(),
              );
            });
          } else {
            formData.append(`sub_questions[${index}][options]`, "");
          }

          const caArr2 = Array.isArray(sq.correct_answer)
            ? sq.correct_answer
            : [String(sq.correct_answer ?? "")];
          caArr2.forEach((val, caIdx) => {
            formData.append(
              `sub_questions[${index}][correct_answer][${caIdx}]`,
              String(val ?? ""),
            );
          });
        });
      }

      if (this.questionImage)
        formData.append("question_image", this.questionImage);
      if (this.questionType === "short_answer" && this.correctAnswerImage) {
        formData.append("correct_answer_image", this.correctAnswerImage);
      }

      let url = "/questions";
      let method = "post";
      if (this.isEditMode) {
        if (!this.form.id) {
          const msg = "Unable to update question: Missing ID";
          this.error = msg;
          this.$emit("show-toast", { message: msg, type: "danger" });
          return;
        }
        url = `/questions/${this.form.id}`;
        method = "post";
        formData.append("_method", "PUT");
      }

      const token = localStorage.getItem("auth_token");

      try {
        const response = await axios({
          method,
          url,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.status === 200 || response.status === 201) {
          this.success = true;
          const msg = this.isEditMode
            ? "Question updated successfully!"
            : "Question created successfully!";
          this.$emit("show-toast", { message: msg, type: "success" });
          this.$emit(this.isEditMode ? "updated" : "created");
        }
      } catch (err) {
        const msg =
          err.response?.data?.message || "An error occurred. Please try again.";
        this.error = msg;
        this.$emit("show-toast", { message: msg, type: "danger" });
        console.error("Error details:", err);
      }
    },
  },
  watch: {
    questionType(newType) {
      if (newType === "true_false") {
        this.form.options = ["true", "false"];
        this.form.correct_answer = [];
        this.form.multiple_answers = false;
      } else if (newType === "short_answer") {
        this.form.options = [""];
        this.form.correct_answer = [""];
        if (this.form.is_math) this.answerText = this.shortAnswerSingle || "";
        this.form.multiple_answers = false;
      } else if (newType === "mcq") {
        if (!Array.isArray(this.form.options) || this.form.options.length < 2) {
          this.form.options = ["", ""];
        }
        if (!Array.isArray(this.form.correct_answer))
          this.form.correct_answer = [];
      } else if (newType === "matching") {
        this.form.multiple_answers = false;
        this.form.matching_items = normalizeMatchingItems(
          this.form.matching_items,
        );
        this.form.matching_pairs = normalizeMatchingPairs(
          this.form.matching_pairs,
        );
      }
    },

    answerText() {
      if (this.questionType === "short_answer" && this.form.is_math) {
        this.shortAnswerSingle = this.answerText;
      }
    },

    questionText() {
      this.updatePreview();
    },

    "form.options": {
      handler(newOptions) {
        if (!["mcq", "true_false"].includes(this.questionType)) return;

        if (this.questionType === "mcq" && this.form.multiple_answers) {
          this.form.correct_answer = (this.form.correct_answer || []).filter(
            (v) => newOptions.includes(v),
          );
          return;
        }

        const current = String(this.form.correct_answer?.[0] ?? "");
        if (current && !newOptions.includes(current)) {
          this.form.correct_answer = [];
        }
      },
      deep: true,
    },

    "form.is_math"(val) {
      if (val) {
        if (!this.questionText) this.questionText = this.form.question || "";
        this.updatePreview();
      }
    },

    "form.multiple_answers"(val) {
      if (this.questionType !== "mcq") return;

      const ca = Array.isArray(this.form.correct_answer)
        ? this.form.correct_answer
        : [];
      if (!val) {
        this.form.correct_answer = ca.length ? [ca[0]] : [];
      } else {
        this.form.correct_answer = ca;
      }
    },
  },
};
</script>

<style scoped>
.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.section-subtitle {
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

/* Hover magnifier for image previews in the question form */
img.img-thumbnail {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: zoom-in;
}

@media (hover: hover) and (pointer: fine) {
  img.img-thumbnail:hover {
    transform: scale(2);
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
}
</style>
